import { Lightbulb24 } from '@honeybook/icons';
import React, { ReactNode, useCallback, useEffect } from 'react';
import colors from '../../styles/colors';
import { vars } from '../../theme';
import type { TAllColorKeys } from '../HBText/HBText';
import styled from 'styled-components';
import {
    ContextualContainer,
    ContextualContentWrapper,
    ContextualDescription,
    ContextualTextWrapper
} from './HBContextualTooltip.styles';
import HBButton from '../HBButton/HBButton';
import HBCloseButton from '../HBCloseButton/HBCloseButton';
import HBText from '../HBText/HBText';
import { isFunction } from 'lodash-es';

export interface Props {
    /** react-joyride callback data containing tooltipProps, skipProps, and primaryProps. */
    joyrideProps: any;
    /** Heading text displayed in the tooltip header; omit for a description-only tooltip. */
    title?: string;
    /** Body content displayed below the title. */
    description: ReactNode;
    /** Fixed pixel width of the tooltip container; auto-sized when omitted. */
    width?: number;
    /** Fixed pixel height of the tooltip container; auto-sized when omitted. */
    height?: number;
    /** Horizontal pixel offset applied to the tooltip container position. */
    horizontalOffset?: number;
    /** Vertical pixel offset applied to the tooltip container position. */
    verticalOffset?: number;
    /** Hides the close button in the tooltip header. */
    hideCloseButton?: boolean;
    /** When true, clicking outside the tooltip does not dismiss it. */
    preventClickOutside?: boolean;
    /** Text for the CTA button; the button is only rendered when this is provided. */
    ctaText?: string;
    /** Called when the CTA button is clicked; receives joyrideProps for custom navigation. Falls back to the close handler if omitted. */
    onCTAClick?(joyrideProps: Props['joyrideProps']): void;
    /** Visual theme: 'dark' renders a blue background with white text, 'light' renders a white background with dark text. */
    theme?: 'dark' | 'light';
}

const HBContextualTooltip = ({
    title,
    description,
    joyrideProps,
    horizontalOffset,
    verticalOffset,
    hideCloseButton,
    preventClickOutside,
    ctaText,
    width,
    onCTAClick,
    theme = 'dark'
}: Props) => {
    const {
        tooltipProps,
        skipProps: { onClick: onCloseClick },
        primaryProps: { onClick: onNextClick }
    } = joyrideProps;

    const handleUserKeyPress = useCallback(
        event => {
            const { key } = event;
            if (key === 'Enter') {
                onNextClick(event);
            }
        },
        [onNextClick]
    );

    const handleUserMouseClick = useCallback(
        event => {
            const tooltipElement: HTMLElement = document.querySelector(
                '#tooltip_container'
            ) as HTMLElement;
            if (
                !tooltipElement.contains(event.target) &&
                !preventClickOutside
            ) {
                onCloseClick(event);
            }
        },
        [onCloseClick, preventClickOutside]
    );

    useEffect(() => {
        window.addEventListener('keydown', handleUserKeyPress);
        return () => {
            window.removeEventListener('keydown', handleUserKeyPress);
        };
    }, [handleUserKeyPress]);

    useEffect(() => {
        window.addEventListener('mouseup', handleUserMouseClick);

        return () => {
            window.removeEventListener('mouseup', handleUserMouseClick);
        };
    }, [handleUserMouseClick]);

    const isDark = theme === 'dark';
    const textColor: TAllColorKeys = isDark
        ? 'text.invertedPrimary'
        : 'text.primary';
    const LightbulbElement = isDark ? (
        <Lightbulb24 color={vars.colors.text.invertedPrimary} />
    ) : (
        <Lightbulb24 color={vars.colors.text.primary} />
    );

    return (
        <ContextualContainer
            {...tooltipProps}
            width={width}
            verticalOffset={verticalOffset}
            horizontalOffset={horizontalOffset}
            $backgroundColor={
                // Unsupported: colors.contextualBlue — dark navy tooltip
                // surface with no semantic equivalent (see .claude/rules/styling.md)
                isDark ? colors.contextualBlue : vars.colors.surface.container
            }
            id="tooltip_container"
        >
            {!hideCloseButton && (
                <HBCloseButton
                    size="xs"
                    distance={8}
                    theme={isDark ? 'light' : 'dark'}
                    onClick={onCloseClick}
                    testId="contextual-tooltip-close"
                />
            )}
            <ContextualContentWrapper>
                {LightbulbElement}
                <ContextualTextWrapper>
                    {title && (
                        <HBText type="emphasis16" color={textColor}>
                            {title}
                        </HBText>
                    )}
                    <ContextualDescription type="body14" color={textColor}>
                        {description}
                    </ContextualDescription>
                </ContextualTextWrapper>
            </ContextualContentWrapper>
            {ctaText && (
                <ButtonContainer>
                    <StyledHBButton
                        buttonTheme="primary-blue"
                        onClick={
                            isFunction(onCTAClick)
                                ? onCTAClick(joyrideProps)
                                : onCloseClick
                        }
                        testId="tooltip-cta--button"
                    >
                        {ctaText}
                    </StyledHBButton>
                </ButtonContainer>
            )}
        </ContextualContainer>
    );
};

export default HBContextualTooltip;

// -------------------------------- Styles -------------------------------- //
const ButtonContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 10px;
`;

const StyledHBButton = styled(HBButton)`
    font-size: 14px;
    height: auto;
    padding: 10px 12px;
`;
